import {
  ActionIcon,
  Center,
  Group,
  NumberInput,
  ScrollArea,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconArrowUpRight,
  IconCalendar,
  IconFileDigit,
  IconForms,
  IconNumber123,
} from '@tabler/icons-react';
import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { useScrollShadow } from 'hooks/use-scroll-shadow';
import { useCompanyConfiguration } from 'init/index';
import { i18nKeys, useTranslation } from 'locales';
import { useHistory } from 'react-router';
import { capitalize, isEmpty } from 'remeda';
import type {
  CompanyConfiguration,
  CustomVariable,
} from 'shared/hooks/use-load-company-configuration';
import { useUpdateClientCustomVariables } from 'shared/hooks/use-update-client-custom-variables';
import { getCurrencySymbolFromCurrencyCode } from 'shared/utils/currency';
import type { CurrencyCode } from 'types/currency';
import type { CustomVariableDisplayType } from 'types/custom-variable-display-type';
import type { Debtor } from 'types/store/client-state';
import { useCustomVariableForm } from './useCustomVariableForm';

type CustomVariablesPanelProps = {
  company: CompanyConfiguration;
  client: Debtor;
};

const renderInputSuffix = (
  displayType: CustomVariableDisplayType | null,
  currency: CurrencyCode,
) => {
  if (displayType === 'money' || displayType === 'money_in_cents')
    return getCurrencySymbolFromCurrencyCode(currency);
  if (displayType === 'percentage') return '%';
  return '';
};

const renderIconByType = (variable: CustomVariable) => {
  switch (variable.var_type) {
    case 'string':
      return <IconForms size={18} stroke={1.5} />;
    case 'number':
      return <IconNumber123 size={18} stroke={1.5} />;
    case 'boolean':
      return <IconFileDigit size={18} stroke={1.5} />;
    case 'date':
      return <IconCalendar size={18} stroke={1.5} />;
    default:
      return null;
  }
};

export const CustomVariablesPanel = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const company = useCompanyConfiguration();
  const debtor = useDebtor();

  const { updateClientCustomVariables, isUpdateClientCustomVariablesLoading } =
    useUpdateClientCustomVariables();

  const { topShadow, bottomShadow, scrollableRef } = useScrollShadow(
    company.custom_variables.length,
  );

  const { form, availableCustomVariables } = useCustomVariableForm({ company, client: debtor });

  const handleSubmit = () => {
    updateClientCustomVariables(
      { id: String(debtor.id), data: form.values },
      { onSuccess: () => form.resetDirty() },
    );
  };

  const renderInputByType = (variable: CustomVariable) => {
    const fieldName = `custom_variables.${variable.column_name}`;

    switch (variable.var_type) {
      case 'string':
        return (
          <TextInput
            variant="filled"
            styles={{
              input: {
                textAlign: 'right',
                color: 'var(--mantine-color-gray-10)',
              },
            }}
            placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.CUSTOM_VARIABLE_TEXT)}
            {...form.getInputProps(fieldName)}
          />
        );
      case 'number':
        return (
          <NumberInput
            variant="filled"
            styles={{
              input: {
                textAlign: 'right',
                color: 'var(--mantine-color-gray-10)',
              },
            }}
            suffix={renderInputSuffix(variable.display, debtor.currency)}
            placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.CUSTOM_VARIABLE_NUMBER)}
            hideControls
            {...form.getInputProps(fieldName)}
          />
        );
      case 'boolean':
        return (
          <Switch
            color="purple.5"
            size="md"
            {...form.getInputProps(fieldName, { type: 'checkbox' })}
          />
        );
      case 'date':
        return (
          <DatePickerInput
            variant="filled"
            styles={{
              input: {
                color: 'var(--mantine-color-gray-10)',
              },
            }}
            placeholder={t(i18nKeys.FORM.SELECT_DATE)}
            {...form.getInputProps(fieldName)}
          />
        );
      default:
        console.warn(`Unsupported variable type: ${variable.var_type}`);
        return null;
    }
  };

  const renderVariableItem = (variable: CustomVariable) => (
    <Group key={variable.id} justify="space-between">
      <Group gap="sm">
        {renderIconByType(variable)}
        <Text>{capitalize(variable.name)}</Text>
      </Group>
      {renderInputByType(variable)}
    </Group>
  );

  const navigateToCustomVariables = () => {
    history.push('/settings/variables/custom-variables');
  };

  const variablesList = availableCustomVariables.map(renderVariableItem);

  if (isEmpty(variablesList))
    return (
      <Panel withBorder title={t(i18nKeys.SETTINGS.CUSTOM_VARIABLES.LONG_TITLE)}>
        <Center>
          <Text c="dimmed" fs="italic">
            No variables
          </Text>
        </Center>
      </Panel>
    );

  return (
    <Panel
      title={t(i18nKeys.SETTINGS.CUSTOM_VARIABLES.LONG_TITLE)}
      topLeft={
        <ActionIcon variant="transparent" onClick={() => navigateToCustomVariables()}>
          <IconArrowUpRight size={18} stroke={1.5} />
        </ActionIcon>
      }
      topRight={
        <SaveButton
          type="submit"
          disabled={!form.isDirty()}
          size="compact-sm"
          variant="light"
          form="customVariablesForm"
          loading={isUpdateClientCustomVariablesLoading}
        />
      }
    >
      <form onSubmit={form.onSubmit(handleSubmit)} id="customVariablesForm">
        <Stack mt="space6">
          {availableCustomVariables.length > 9 ? (
            <ScrollArea h="55vh" scrollbarSize={6} viewportRef={scrollableRef}>
              {topShadow}
              {bottomShadow}
              <Stack pr="md">{variablesList}</Stack>
            </ScrollArea>
          ) : (
            variablesList
          )}
        </Stack>
      </form>
    </Panel>
  );
};
